import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { VisuallyHidden } from 'radix-ui';
import { BlueprintCreateForm } from './BlueprintCreateForm';
import type { BlueprintPrefillRepoState } from '@/lib/blueprints';

export interface BlueprintCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Repo to seed the streamlined repo-image flow with. */
  prefillRepo?: BlueprintPrefillRepoState;
}

/**
 * The blueprint create form in a modal, for the places that nudge toward a
 * blueprint from somewhere else in the app (the launch dialog and agent header
 * "no blueprint for this repo" warning). Building the blueprint here keeps the
 * user on the page they were working on instead of navigating them away to
 * `blueprints/new` mid-task.
 *
 * The form supplies its own header, footer, and internal scrolling, so the
 * dialog contributes only the surface: no padding, no close button (the footer
 * already has Cancel), and the height the form's body scrolls within.
 */
export function BlueprintCreateDialog({
  open,
  onOpenChange,
  prefillRepo,
}: BlueprintCreateDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-[min(85vh,44rem)] gap-0 overflow-hidden p-0 sm:max-w-2xl"
        showCloseButton={false}
        data-testid="blueprint-create-dialog"
      >
        {/* The form renders its own visible title and blurb; these satisfy
            Radix's accessible name/description without duplicating them. */}
        <VisuallyHidden.Root>
          <DialogTitle>Create Blueprint</DialogTitle>
          <DialogDescription>
            Build a prebuilt devbox image that agents can launch from.
          </DialogDescription>
        </VisuallyHidden.Root>
        {/* The dialog takes a definite height so the form's `h-full` layout
            resolves: body scrolls, footer stays pinned to the bottom. A
            content-height dialog would leave the body unbounded and push the
            footer out of the modal entirely. */}
        <BlueprintCreateForm
          prefillRepo={prefillRepo}
          onCancel={() => onOpenChange(false)}
          onCreated={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
